import { useCallback, useEffect, useRef, useState } from "react";
import { getWaterEvents } from "services/greenhouseApi";
import { useStableState } from "utils/stableState";

// An operation moves through photo -> pump -> settle -> photo in a couple of minutes, so poll fast
// while one is in flight and slowly otherwise.
export const BUSY_POLL_MS = 4000;
export const IDLE_POLL_MS = 15000;
const TERMINAL = new Set(["done", "skipped", "failed", "cancelled"]);

// The controller's pump operations (scheduled and manual) with their before/after photos, newest
// first. `mac` is the controller (mac_slug or MAC); nothing is fetched without one.
export function usePumpOperations(mac, { limit = 100 } = {}) {
  const [events, setEvents] = useStableState([]);
  const [error, setError] = useState(null);
  const busyRef = useRef(false);
  const errorShownRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!mac) return;
    try {
      const result = await getWaterEvents(mac, { limit });
      const list = (result.events || []).slice().reverse();
      busyRef.current = list.some((event) => !TERMINAL.has(event.state));
      setEvents(list);
      if (errorShownRef.current) {
        errorShownRef.current = false;
        setError(null);
      }
    } catch (requestError) {
      errorShownRef.current = true;
      setError(requestError.message);
    }
  }, [mac, limit, setEvents]);

  useEffect(() => {
    if (!mac) {
      setEvents([]);
      return undefined;
    }
    let active = true;
    let timer = null;
    const tick = async () => {
      await refresh();
      if (active) timer = setTimeout(tick, busyRef.current ? BUSY_POLL_MS : IDLE_POLL_MS);
    };
    tick();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [mac, refresh, setEvents]);

  return { events, error, refresh };
}
